/**
 * Liquiditätsplanung v1 (#1315) — domain types.
 *
 * INPUT types mirror the UDB ``twin_derived_facts`` ``fact_value`` JSON
 * (snake_case), frozen in the #1315 data contract (see the task's
 * plan.md §1). The subtask-04 hook narrows the generic
 * ``DerivedFact.fact_value: Record<string, unknown>`` from twinService
 * into these shapes.
 *
 * OUTPUT types are the view model the chart / verdict line / ledger
 * consume (camelCase) — see computeBalanceSeries.
 */

// ── Inputs (wire format) ──────────────────────────────────────────────

import type { LedgerCategory } from './ledgerCategory';

export type Direction = 'income' | 'expense';

/** ``fact_type: 'liquiditaet_config'`` — one row per tenant. */
export interface LiquiditaetConfig {
  anchor_date: string; // 'YYYY-MM-DD' — the known-balance boundary (e.g. WJ end)
  anchor_balance_eur: number; // authoritative balance on anchor_date
  /** Credit line (Kontokorrent-Limit), typically negative. ``null`` = pending the farmer's input. */
  kk_limit_eur: number | null;
  wj_start_month: number; // 1–12; 7 = Juli (Wirtschaftsjahr start)
}

/** ``fact_type: 'liquiditaet_planned_transaction'`` — one per plan row. */
/**
 * HOF-135 §3 — the plan sources that are a live WRITE-OF-RECORD: they survive the
 * native-record merge (``mergeRecords``) and read through every mapper as themselves.
 * ONE constant, shared with the type unions, so adding a source (the ARCH.md seam doc
 * already forecasts finAPI) cannot be threaded through two of the three ===-chains and
 * silently collapsed by the third — the drift bit INSIDE the PR that introduced
 * ``bwa``/``model`` (codex round 2, finding 6). The Excel seed is deliberately not
 * here, and the D2 overlay filters (``userPlansFrom`` etc.) stay ``=== 'user'``.
 * Mirror of the Python ``liquiditaet_reads.PLAN_WRITE_OF_RECORD_SOURCES``.
 */
export const PLAN_WRITE_OF_RECORD_SOURCES = ['user', 'bwa', 'model'] as const;
export type PlanWriteOfRecordSource = (typeof PLAN_WRITE_OF_RECORD_SOURCES)[number];

export function isPlanWriteOfRecordSource(v: unknown): v is PlanWriteOfRecordSource {
  return (PLAN_WRITE_OF_RECORD_SOURCES as readonly unknown[]).includes(v);
}

export interface PlannedTransaction {
  kind: 'planned';
  /** Categorisation on the 2-level GuV ledger_category axis (see ``ledgerCategory.ts``).
   *  The 9 document domains are no longer stored — only this L2 category id is. */
  ledger_category: LedgerCategory;
  label: string;
  direction: Direction;
  /** Brutto, sign-free total (= ``qty × unit_price_eur``) — ``direction`` carries the sign. */
  amount_eur: number;
  /** Volume/quantity behind the total; ``1`` when the line has no natural quantity. */
  qty?: number;
  /** Price per unit; ``= amount_eur`` when ``qty`` is 1. */
  unit_price_eur?: number;
  value_date: string; // 'YYYY-MM-DD' (default day 15 of period)
  period_ym: string; // 'YYYY-MM'
  qty_meta?: { ha?: number; t?: number; eur_per_t?: number };
  /** HOF-135 §3 — ``bwa``/``model`` let a generated plan say what it is instead of
   *  impersonating a farmer's entry. NOT merely descriptive: the D2 account-less
   *  overlay selects on ``source === 'user'`` (``engineRecordsFrom`` /
   *  ``userPlansFrom``), so a row's source decides how it is ROUTED. */
  source: 'ws26_27_excel' | PlanWriteOfRecordSource;
  /** HOF-135 §3 — plan provenance, all optional and absent on every pre-HOF-135 row.
   *  Before this the only provenance was prose in ``notes``, which is why WF's
   *  asymmetric rebase (revenue ×1.0848 vs costs ×0.8899) and its −2.521.555 €
   *  plug were invisible to every surface. */
  method?: string;
  factor?: number;
  lag_days?: number;
  /** This row balances the model rather than describing a business event. WF's nine
   *  „Residuum ggü. Kontoauszug" rows ARE the Engpass — without them the projected
   *  year is +187.625 € — and they rendered as ordinary „Investitionen". */
  is_plug?: boolean;
  assumption?: string;
  base_ref?: string;
  derived_fact_id?: string;
  /** Free-text note (rationale, assumptions, deviations) — shown in the row's
   *  unfolded view and editable in the form. */
  notes?: string;
  /** The owning bank account (#2211). OPTIONAL — set by the multi-account write
   *  path (subtask 12/seam b); single-account tenants + legacy plans omit it and
   *  route to the overlay-primary account via the D2 read-side fallback. Read back
   *  by the modal (pre-select the Konto) and by ``accountPartition`` (route the plan
   *  onto its own account's series). */
  account_id?: string;
  /** The Gegenpartei this transaction is with (#2678 c) — an ``organization``
   *  entity id, picked by the farmer in the form's Gegenpartei field. OPTIONAL:
   *  absent on every legacy plan and on any row nobody has linked. Read back so the
   *  form can pre-select it (and so an edit re-sends it through the wholesale
   *  fact_value overwrite). Distinct from the raw ``BankTransaction.counterparty``
   *  free text, which is what the BANK wrote, not who it was. */
  counterparty_id?: string;
}

/** ``fact_type: 'liquiditaet_bank_transaction'`` — one per Kontoauszug line. */
export interface BankTransaction {
  kind: 'fact';
  booking_date: string; // 'YYYY-MM-DD'
  amount_eur: number; // signed
  counterparty: string;
  purpose: string;
  statement_id: string;
  period_ym: string;
  /** Pre-categorised flows only (#2161, DATEV backfill) — absent on legacy facts. */
  ledger_category?: string;
  /** The owning bank account (#2211). The worker materialiser stamps this on every
   *  bank fact from the source ``cash_flow`` (its FK is ontology-required), so it is
   *  present on the wire for every WF row; the feed reads it to scope by Konto.
   *  Absent only on a defensive account-less fact (→ overlay-primary bucket). */
  account_id?: string;
  /** The expectation this credit SETTLES (#3137) — a ``cash_flow`` entity id, so
   *  ``cf-``-prefixed. Written by ``wibill_settlement_match`` onto the flow and
   *  carried onto the fact by the worker materialiser; ``bankToRecord`` strips the
   *  prefix and uses it as the ``transaction_id`` so the expectation and its credit
   *  become ONE ledger row. Absent on every unsettled line — and on EVERY fact
   *  minted before the #3137 re-materialise. */
  planned_ref?: string;
  /** The resolved counterparty link (#3160 — the FE half of #3142's two-mirror
   *  chain): an ``organization`` entity id the worker materialiser carries onto
   *  the fact from the source ``cash_flow``. Distinct from the free-text
   *  ``counterparty`` above, which is what the BANK wrote, not who it was.
   *  Absent on every fact minted before the #3161 re-materialise — the absent
   *  case is the norm and must never become ``''``. */
  counterparty_id?: string;
  /** HOW the link was established (#3142 provenance) — closed vocabulary declared
   *  in ``config/ontology/cash_flow.yaml`` (``invoice_reference`` | ``curated_map``),
   *  treated here as an opaque pass-through: verbatim string, no validation, no
   *  case-folding (the worker refuses undeclared values at the write side). Only
   *  ever present WITH ``counterparty_id`` — absence means „nicht maschinell
   *  zugeordnet", never „ohne Gegenpartei"; a farmer's pick is the ABSENCE of a
   *  source, so a deriving rung can never launder a guess into a human decision. */
  counterparty_id_source?: string;
  /** The Notiz on this booking (#3187) — free text a farmer or an annotation agent
   *  wrote ABOUT the line, carried onto the fact by the worker materialiser from the
   *  source ``cash_flow``. The entity attribute is ``note`` (singular); the wire key
   *  is ``notes`` (plural), renamed ONCE at the materialiser so it matches
   *  ``LiquiditaetRecord.notes`` and the planned/record fact families — see
   *  worker/ARCH.md § "note → notes: the three mirrors (#3187)".
   *
   *  Distinct from ``purpose`` above, which is the BANK's own text: every
   *  categorisation rung reads ``purpose``, so the two must never be merged
   *  (``config/ontology/cash_flow.yaml``). Absent on every fact minted before the
   *  #3187 re-materialise — the absent case is the norm and must never become ``''``.
   *  ``bankToRecord`` lifts it onto the record, where ``LedgerFeed`` renders it in
   *  the unfolded „Notiz" block. */
  notes?: string;
}

/** ``fact_type: 'liquiditaet_statement_balance'`` — one per Kontoauszug. */
export interface StatementBalance {
  statement_id: string;
  start_date: string; // 'YYYY-MM-DD'
  end_date: string; // 'YYYY-MM-DD'
  /** ``null`` = the statement states no such Saldo. NOT 0,00 — see ``toStatement``. */
  anfangssaldo_eur: number | null;
  endsaldo_eur: number | null;
  /** The Konto this Auszug attests to (#2690). NOT a ``fact_value`` field — the
   *  #2004 lock keeps an account key out of this family, so the account arrives on
   *  the fact ANCHOR (``entity_type='financial_account'``) and ``toStatement``
   *  lifts it here. Same optional shape as ``BankTransaction.account_id`` above:
   *  absent on the seed-era rows (anchored on ``entity_type='liquiditaet'``), which
   *  keep routing to the alias account via the read-side fallback. */
  account_id?: string;
  /** HOF-97 (A) — the write-side verdict on whether this Endzeile may be believed.
   *  A multi-page Kontoauszug is split into page-range child documents, and the
   *  extractor reads each fragment's page-bottom „Übertrag auf Blatt N" as that
   *  fragment's closing balance; all of them are then stamped with the statement's
   *  own end_date. WF's Konto 64036203 holds ten contradictory 30.06.2026 values
   *  that way. ``_mark_contested_endzeilen`` marks such a group on the entity and
   *  the materialiser carries the flag onto the fact; ``statementAnchors`` refuses
   *  to re-anchor on it. Absent means UNRECORDED (every fact minted before this
   *  shipped), which is a different claim from ``false``. */
  contested?: boolean;
  /** HOF-97 — this row named no account and was ADOPTED by the alias run so it could
   *  still advance that account's Ist horizon (``statementsForAccount``). Adoption is a
   *  generosity about a HORIZON and is not evidence about a BALANCE: the row may attest
   *  to a different Konto, or be a legacy aggregate over several. ``statementAnchors``
   *  therefore refuses to re-seed on it. */
  anchor_adopted?: boolean;
}

// ── Outputs (view model) ──────────────────────────────────────────────

export interface DailyBalancePoint {
  date: string; // 'YYYY-MM-DD'
  balanceEur: number; // end-of-day balance
  isActual: boolean; // true for history (date <= anchor_date), false for projection
}

export type VerdictLevel = 'red' | 'yellow' | 'green';

export interface MonthVerdict {
  periodYm: string; // 'YYYY-MM'
  minBalanceEur: number; // intra-month minimum (lowest EOD balance in the month)
  minDate: string; // 'YYYY-MM-DD' of that minimum
  level: VerdictLevel;
}

export interface Verdict {
  level: VerdictLevel;
  sentence: string; // German one-liner for the verdict line
  firstProblemYm: string | null; // earliest non-green month, null when all green
  firstProblemDate: string | null; // date of the minimum within that month
  firstProblemAmountEur: number | null; // the balance at that minimum
  kkLimitEur: number | null;
  monthly: MonthVerdict[]; // per future month (drives lens accents)
}

export interface BalanceSeriesResult {
  dailySeries: DailyBalancePoint[];
  verdict: Verdict;
  /** = ``verdict.firstProblemYm`` — surfaced for the lens default selection. */
  firstProblemMonth: string | null;
  /**
   * The Ist/Prognose boundary (#1639): the last day rendered as ACTUAL (solid).
   * Derived as ``max(anchor_date, latest payed booking_date, latest
   * statement_balance end_date)``, capped at ``today`` (a post-dated payed never
   * draws solid past Heute) and floored at ``anchor_date`` (no actuals beyond the
   * anchor collapses it back to the seed). The caption (``Ist bis …``) and the
   * ledger greying read THIS instead of re-deriving from ``anchor_date``, so a new
   * ``payed`` record advances the boundary with no config edit. ``''`` for the
   * empty/neutral series.
   */
  istHorizon: string;
  /**
   * Does this account PUBLISH an ``istHorizon`` strictly past its own
   * ``anchor_date`` (#3241)? Usually that means it holds a payed ``booking_date``
   * or ``statement_balance.end_date`` after its anchor — but the published
   * horizon is what counts, not the raw evidence (see the caveat below).
   *
   * ``false`` means the account contributed no post-anchor actuals at all, so its
   * ``istHorizon`` is just the anchor seed echoed back — it asserts nothing about
   * how current the farm's data is. ``aggregateSeries`` uses this to keep such an
   * account OUT of the farm-wide MIN boundary; see the rule + its #2611
   * reconciliation in ``aggregateSeries.ts``.
   *
   * Read from the PUBLISHED ``istHorizon`` (after its caps), not the raw
   * evidence: a future ``anchor_date`` clamps a post-anchor actual back onto the
   * anchor, and an account publishing its anchor is by definition not advanced.
   */
  istHorizonAdvanced: boolean;
  /**
   * The boundary that FORECAST JUDGEMENT starts after (#3241) — the verdict, the
   * breach day, the lowest forecast value and the overrun. Always ``<=``
   * ``istHorizon``.
   *
   * For a per-account series the two are identical. They diverge only on the
   * "Alle Konten" aggregate, where #3241 widens the rendered ``istHorizon`` past a
   * dormant account's own horizon so the farm's real actuals stop drawing as
   * Prognose. That account's curve is still summed into the total, so anything
   * that judges RISK must keep using the conservative boundary — otherwise a
   * planned outflow landing inside the widened window is silently dropped from
   * the projection and its Engpass never reaches the Ampel or the headline.
   *
   * Rule of thumb: rendering the Ist/Prognose split reads ``istHorizon``;
   * deciding whether the farmer is in trouble reads ``verdictHorizon``.
   */
  verdictHorizon: string;
  /**
   * HOF-97 §6 L5.3 — one entry per statement period the series re-anchored on, in
   * date order. ``residualEur = endsaldoEur − expectedEur`` is a DIRECT measurement
   * of how much bank data is missing or wrong for that period: our integral said one
   * thing, the bank's own statement of record says another, and the gap is the
   * extraction/routing loss. Before this the number existed nowhere and the farmer
   * discovered it by adding up her Kontoauszüge by hand.
   *
   * Empty when nothing could be reconciled — no statement, or every candidate
   * contested. That is NOT the same as "all residuals were zero"; see ``reconciled``.
   */
  statementResiduals: StatementResidual[];
  /**
   * Three-valued on purpose (§6 L5.4). ``true`` = every statement period reconciled
   * within tolerance; ``false`` = at least one drifted; **``null`` = never checked
   * against a bank statement at all.**
   *
   * The null state is the point. Conflating "unchecked" with "clean" is exactly how
   * an unreconciled integral passed for a reconciled balance — so a surface rendering
   * this must show an unknown, never a tick.
   */
  reconciled: boolean | null;
  /** HOF-97 — which Konto this series is for, stamped by the per-account caller so
   *  ``sumBalanceSeries`` can name the account each residual came from. The engine runs
   *  per account precisely so it never has to know which one it is; threading the id
   *  through it would undo that. Absent on the aggregate itself. */
  accountId?: string;
}

/** One statement period's reconciliation result (HOF-97 §6 L5.3). */
export interface StatementResidual {
  statementId: string;
  endDate: string;
  /** What the bank's Endzeile says the balance was. */
  endsaldoEur: number;
  /** What our integral had reached the moment before the re-seed. */
  expectedEur: number;
  /** ``endsaldoEur − expectedEur`` — positive means bookings are MISSING from our data. */
  residualEur: number;
  /** Which Konto drifted. Stamped by ``aggregateSeries`` when folding the total; the
   *  per-account engine never needs it, since the caller already knows. */
  accountId?: string;
}

// ── Plan↔Ist reconciliation (#1414) ───────────────────────────────────

/**
 * The ✓⏳⚠＋ states from #1315/#1414.
 *  - ``bestaetigt``  ✓ plan ↔ bank linked, amounts agree within tolerance
 *  - ``abweichend``  ⚠ matched but actual ≠ plan beyond tolerance (Δ shown)
 *  - ``geplant``     ⏳ unmatched plan line; still projected
 *  - ``ungeplant``   ＋ bank line with NO commitment of either kind (#3026) →
 *                     categorize / accept
 *
 * #3026 widened the commitment a booking is judged against from ``planned`` alone
 * to ``planned ?? contracted`` — see ``transactions.transactionState``, where the
 * two axes carry deliberately different (two-sided vs floor) rules.
 */
export type ReconState = 'bestaetigt' | 'abweichend' | 'geplant' | 'ungeplant';

/** Reconciliation metadata attached to a ledger row (#1414). */
export interface ReconInfo {
  state: ReconState;
  /** Signed actual (bank) amount — present on matched rows. */
  istAmountEur?: number;
  /** ``istAmountEur − commitmentSigned`` — present on matched rows. */
  deltaEur?: number;
  /** WHICH commitment the state/Δ were reached against (#3026). The chip renders
   *  from ``ReconInfo`` alone, so without this it cannot tell a farmer "Plan und Ist
   *  stimmen überein" from "Kontrahiert und Ist stimmen überein" — and on a settled
   *  buyer credit there is no Plan to speak of. Absent on an uncommitted row. */
  commitment?: 'planned' | 'contracted';
  plannedId?: string;
  /** Linked bank-fact id(s); >1 for an m:n (several plans settled by one line). */
  bankId?: string;
}

// ── Transaction lifecycle records (transaction-records task) ───────────

/** Lifecycle stage of a record within a transaction. */
export type RecordRole = 'planned' | 'contracted' | 'payed';

/**
 * ``fact_type: 'liquiditaet_record'`` — one fact per lifecycle record. Records
 * sharing a ``transaction_id`` are the planned / contracted / payed views of the
 * SAME logical transaction (grouped at read time by ``groupRecords``).
 * ``transaction_id`` and ``record_role`` are TOP-LEVEL in ``fact_value`` so the
 * UDB ``attr_in`` filter (top-level string-equality only) can target them.
 */
export interface LiquiditaetRecord {
  transaction_id: string;
  record_role: RecordRole;
  /** Amount/quantity; ``1`` when the line has no natural quantity. */
  qty: number;
  /** Price per unit; ``= total_eur`` when ``qty`` is 1. */
  unit_price_eur: number;
  /** Authoritative money figure (= qty × unit_price_eur), sign-free — ``direction`` carries the sign. */
  total_eur: number;
  value_date: string; // 'YYYY-MM-DD'
  ledger_category: LedgerCategory;
  direction: Direction;
  label: string;
  period_ym: string; // 'YYYY-MM'
  /** planned-only provenance. HOF-135 §3 widens it in lockstep with
   *  ``PlannedTransaction.source``: the record set is a PIVOT the planned rows travel
   *  through (``plannedToRecord`` → ``groupRecords`` → ``recordToPlanned``), so a
   *  narrower union here silently collapsed ``bwa``/``model`` back to the Excel seed
   *  one hop after the mapper read them through (codex Angle-B finding 5). The
   *  ``source === 'user'`` filters (``mergeRecords``, ``userPlansFrom``) are exact
   *  string matches, so the widening cannot leak a generated row into the D2 overlay. */
  source?: 'excel' | PlanWriteOfRecordSource;
  /** HOF-135 §3 — plan provenance riding the pivot (see ``PlannedTransaction``); the
   *  chip and the edit-modal seed both read ``row.planned``, which is REBUILT from
   *  this record, so a field absent here is a field the page cannot show. */
  method?: string;
  factor?: number;
  lag_days?: number;
  is_plug?: boolean;
  assumption?: string;
  base_ref?: string;
  /** contracted-only: archive document / Rechnung-Gutschrift ref. */
  doc_ref?: string;
  /** payed-only: source Kontoauszug. */
  statement_id?: string;
  /** Free-text note (rationale, assumptions, deviations) — shown in the row's
   *  unfolded view; per-record so a plan note and a deviation note can differ. */
  notes?: string;
  /** Stable per-record id (the ``derived_fact_id``). */
  derived_fact_id?: string;
  /**
   * The owning bank account (#2005 display dimension). OPTIONAL so the legacy
   * fact path + every existing fixture compile untouched — the fact stores have
   * no account dimension. Populated only when a record is derived from a
   * ``cash_flow`` entity partitioned by ``account_id`` (see ``accountPartition``);
   * it drives the per-account balance runs, never the fact-sourced ledger. The
   * Python cash-movement atom does carry ``account_id``; this mirrors it on the
   * TS read side purely for the multi-account UI, not for the engine math.
   */
  account_id?: string;
  /** The farmer-picked Gegenpartei (#2678 c) — an ``organization`` entity id.
   *  Threaded for the SAME reason as ``account_id`` above: this record type is the
   *  pivot the whole feed passes through (`toPlanned → plannedToRecord →
   *  groupRecords → recordToPlanned → row.planned`), and the edit modal seeds its
   *  form state from the far end of that chain. A field the record does not carry
   *  is a field the form cannot show — and, because the upsert overwrites
   *  ``fact_value`` wholesale, one the next save silently deletes. */
  counterparty_id?: string;
  /** Provenance of ``counterparty_id`` when the link was MACHINE-derived (#3160,
   *  mirroring the Python ``bank_to_record`` — see ``BankTransaction``'s twin
   *  field for the vocabulary and the opaque-pass-through rule). Only bank
   *  ``payed`` records ever carry it: a plan's link is a farmer's pick, and a
   *  source is deliberately structurally absent there (``PlannedTransaction``
   *  has no such field, matching the Python ``to_planned`` mirror) — so readers
   *  must take id + source off the SAME record, never pair one record's id with
   *  a sibling's source (``LedgerFeed.counterpartyOf``). */
  counterparty_id_source?: string;
  /** Contracted-only: the date a bank ACTUAL settled this expectation (#3065). A
   *  collective 1:N buyer credit stamps this on EVERY expectation it settles, but
   *  only the first gets a ``payed`` sibling (the #3026-era one-``payed``-per-
   *  transaction guard) — so a `contracted`-only record 2..N whose `settled_at` is
   *  set is money that already arrived, not a genuinely open, unpaid receivable.
   *  Single-home field (only ever written onto the ``cash_flow`` entity attribute,
   *  never a retained fact), unlike `counterparty_id`'s two-home split. */
  settled_at?: string;
}

/**
 * The grouped view model: one logical transaction with up to three lifecycle
 * records. Canonical attributes (label/ledger_category/direction) come from the
 * planned record if present, else the earliest record by ``value_date``.
 */
export interface Transaction {
  id: string; // = transaction_id
  label: string;
  ledger_category: LedgerCategory;
  direction: Direction;
  planned?: LiquiditaetRecord;
  contracted?: LiquiditaetRecord;
  payed?: LiquiditaetRecord;
}
